import styled from '@emotion/styled'
import { colors } from '../../__styling/settings/colors'
import { fonts } from '../../__styling/settings/fonts'
import { breakpoint } from '../../__styling/style-functions'
import { calcRem } from '../../__styling/style-functions/calc-rem'
import { zIndex } from '../../__styling/settings/z-index'

/** Circular wrapper for speaker icon only: 30×30, same as StyledAuthorThumbnail, theme primary + white icon */
export const StyledTtsButtonIconCircle = styled('span')(({ theme }) => {
    const primaryBg =
        theme?.colors?.brand ??
        (theme?.colors?.palette as { red?: string } | undefined)?.red ??
        colors.sevennews.neonSilver30
    const iconWhite =
        theme?.colors?.palette?.white ?? colors.white
    return {
        width: calcRem(30),
        height: calcRem(30),
        borderRadius: '50%',
        background: primaryBg,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        '& svg': {
            width: calcRem(18),
            height: calcRem(18),
            display: 'block',
        },
        '& svg path': {
            fill: iconWhite,
        },
    }
})

/** Text part only (no box); pill is the button container so circle overlaps pill left edge, byline-style */
export const StyledTtsButtonLabel = styled('span')({
    color: colors.sevennews.bauhausBlack80,
    fontFamily: fonts.sevennews.sansSerif,
    fontSize: calcRem(12),
    fontWeight: 500,
})

/** "Listen to article" button: single pill container (like StyledArticleByline), circle inside at left overlapping pill */
export const StyledTtsButton = styled('button')({
    appearance: 'none',
    border: 'none',
    height: calcRem(30),
    background: colors.sevennews.neonSilver30,
    borderRadius: calcRem(22),
    paddingLeft: 0,
    paddingRight: calcRem(16),
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: calcRem(8),

    '&:hover': {
        background: 'rgba(208, 203, 202, 0.5)',
    },
    '&:focus-visible': {
        outline: '2px solid currentColor',
        outlineOffset: 2,
    },
})

/** Wrapper for TTS custom player bar (contains hidden audio + TtsPlayerBar). */
export const StyledTtsPlayerMinimal = styled('section')({
    position: 'relative',
    marginBottom: calcRem(24),
})

/** Custom TTS bar: pill shape, light grey background, rounded. */
export const StyledTtsPlayerBar = styled('section')({
    display: 'flex',
    alignItems: 'center',
    gap: calcRem(12),
    height: calcRem(48),
    padding: `0 ${calcRem(8)} 0 ${calcRem(6)}`,
    borderRadius: calcRem(24),
    background: colors.designSystem.lightGrey,
    minWidth: 0,
})

/** Hidden native audio: keep in DOM for API, not visible. */
export const StyledTtsAudioHidden = styled('div')({
    position: 'absolute',
    width: 0,
    height: 0,
    opacity: 0,
    pointerEvents: 'none',
    overflow: 'hidden',
})

/** Play/Pause: black circle, white icon. */
export const StyledTtsPlayPauseButton = styled('button')({
    appearance: 'none',
    border: 'none',
    width: calcRem(40),
    height: calcRem(40),
    borderRadius: '50%',
    background: colors.black,
    color: colors.white,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    padding: 0,
    position: 'relative',
    '&:hover': {
        background: colors.darkGrey,
    },
    '&:focus-visible': {
        outline: '2px solid currentColor',
        outlineOffset: 2,
    },
    /* Icon absolutely centered; play triangle (viewBox 19×23) is left-of-center, so nudge 2px right, 1px down */
    '& svg': {
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        margin: 'auto',
        width: calcRem(18),
        height: calcRem(18),
        display: 'block',
    },
    '&[data-icon="play"] svg': {
        transform: 'translate(2px, 1px)',
    },
    '& svg path': {
        fill: 'currentColor',
    },
})

/** Playback speed trigger + dropdown container. */
export const StyledTtsSpeedWrap = styled('div')({
    position: 'relative',
    flexShrink: 0,
})

export const StyledTtsSpeedButton = styled('button')({
    appearance: 'none',
    border: 'none',
    background: 'transparent',
    color: colors.black,
    fontFamily: 'inherit',
    fontSize: calcRem(14),
    fontWeight: 500,
    padding: `${calcRem(6)} ${calcRem(8)}`,
    borderRadius: calcRem(6),
    cursor: 'pointer',
    minWidth: calcRem(40),
    '&:hover': {
        background: 'rgba(0, 0, 0, 0.06)',
    },
    '&:focus-visible': {
        outline: '2px solid currentColor',
        outlineOffset: 2,
    },
})

export const StyledTtsSpeedDropdown = styled('div')({
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: calcRem(4),
    background: colors.white,
    borderRadius: calcRem(8),
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    padding: calcRem(4),
    zIndex: 10,
    minWidth: calcRem(72),
    maxHeight: calcRem(200),
    overflowY: 'auto',
})

export const StyledTtsSpeedOption = styled('button')({
    appearance: 'none',
    border: 'none',
    background: 'transparent',
    color: colors.black,
    fontFamily: 'inherit',
    fontSize: calcRem(14),
    width: '100%',
    padding: `${calcRem(8)} ${calcRem(12)}`,
    borderRadius: calcRem(4),
    cursor: 'pointer',
    textAlign: 'left',
    '&:hover': {
        background: 'rgba(0, 0, 0, 0.06)',
    },
    '&[data-selected="true"]': {
        fontWeight: 600,
        background: 'rgba(0, 0, 0, 0.08)',
    },
})

/** Time display: current / total (MM:SS). */
export const StyledTtsTime = styled('span')({
    color: colors.black,
    fontFamily: 'inherit',
    fontSize: calcRem(13),
    fontWeight: 500,
    flexShrink: 0,
})

/** Progress bar track. */
export const StyledTtsProgressTrack = styled('div')({
    flex: 1,
    minWidth: 0,
    height: calcRem(6),
    borderRadius: calcRem(3),
    background: 'rgba(0, 0, 0, 0.12)',
    cursor: 'pointer',
    position: 'relative',
})

/** Progress bar fill (played portion). */
export const StyledTtsProgressFill = styled('div')<{ percent: number }>(
    ({ percent }) => ({
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: `${Math.max(0, Math.min(100, percent))}%`,
        borderRadius: calcRem(3),
        background: 'rgba(0, 0, 0, 0.35)',
        pointerEvents: 'none',
    }),
)

/** Close button for TTS bar (used inside TtsPlayerBar). */
export const StyledTtsCloseButton = styled('button')({
    appearance: 'none',
    border: 'none',
    background: 'transparent',
    color: 'currentColor',
    cursor: 'pointer',
    padding: 0,
    borderRadius: calcRem(4),
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: calcRem(40),
    height: '100%',

    '&:hover': {
        background: 'rgba(0, 0, 0, 0.06)',
    },
    '&:focus-visible': {
        outline: '2px solid currentColor',
        outlineOffset: 2,
    },

    '& svg': {
        width: calcRem(16),
        height: calcRem(16),
    },
})

export const StyledFloatingTtsControls = styled('div')<{
    top?: number
    left?: number
}>(({ top, left }) => ({
    position: 'fixed', // Always use fixed positioning for viewport-relative placement
    // If top/left are provided, position below current paragraph
    // Otherwise, use fallback position (bottom right)
    ...(top !== undefined && left !== undefined
        ? {
              top: `${top}px`,
              left: `${left}px`,
              transform: 'translateX(-50%)', // Center horizontally
          }
        : {
              right: calcRem(16),
              bottom: calcRem(16),
          }),
    zIndex: zIndex.common.floatingTtsControls,
    display: 'flex',
    alignItems: 'center',
    gap: calcRem(10),
    padding: `${calcRem(10)} ${calcRem(12)}`,
    borderRadius: calcRem(999),
    background: 'rgba(10, 20, 40, 0.85)',
    color: colors.white,
    pointerEvents: 'auto', // Ensure it's always clickable
    // Ensure it's visible and clickable on mobile
    [breakpoint('sm')]: {
        padding: `${calcRem(8)} ${calcRem(10)}`,
        gap: calcRem(8),
    },
    // Add backdrop for better visibility
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    // Ensure it's always on top
    isolation: 'isolate', // Create new stacking context
}))

export const StyledFloatingTtsButton = styled('button')({
    appearance: 'none',
    border: 0,
    cursor: 'pointer',
    padding: `${calcRem(8)} ${calcRem(12)}`,
    borderRadius: calcRem(999),
    background: colors.sevennews.red,
    color: colors.white,
    fontWeight: 700,
    fontSize: calcRem(12),
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    pointerEvents: 'auto', // Ensure button is always clickable
    position: 'relative', // Ensure it's in the stacking context
    zIndex: 1, // Ensure button is above the container
    // Mobile responsive styles
    [breakpoint('sm')]: {
        padding: `${calcRem(6)} ${calcRem(10)}`,
        fontSize: calcRem(11),
    },
    // Ensure touch targets are large enough on mobile
    minWidth: calcRem(60),
    minHeight: calcRem(36),
    touchAction: 'manipulation', // Improve touch responsiveness
})
